<template>
    <div class="ppv2-w-full">
        <div
            v-for="(d, di) in draftDays"
            :key="d.name"
            class="ppv2-grid"
            :class="di === 0 ? '' : 'ppv2-border-t'"
            :style="dayRowStyle(di)"
        >
            <!-- Toggle Ouvert/Fermé + nom du jour -->
            <div class="ppv2-flex ppv2-items-center ppv2-gap-[11px]" style="padding-top: 6px">
                <button
                    type="button"
                    role="switch"
                    class="sde-switch"
                    :class="{ 'sde-switch--on': d.on }"
                    :aria-checked="d.on ? 'true' : 'false'"
                    :aria-label="d.label"
                    @click="toggleDay(d)"
                >
                    <span class="sde-switch__knob"></span>
                </button>
                <span class="sde-day-name poppinsMedium" :class="{ 'sde-day-name--off': !d.on }">{{ d.label }}</span>
            </div>

            <!-- Colonne créneaux (ou fermé) + bannière de la nuit précédente -->
            <div class="ppv2-flex ppv2-flex-col" style="gap: 6px">
                <div v-if="getPreviousDayNight(di)" class="sde-night-banner">
                    <Moon :size="14" class="ppv2-flex-shrink-0" style="color: #9aa4b2" />
                    <span>
                        {{ $t('experience.step4.multiMode.schedule.nightFrom') }}
                        {{ getPreviousDayNight(di).dayLabel.toLowerCase() }} · 00:00 → {{ getPreviousDayNight(di).endTime }} —
                        {{ $t('experience.step4.multiMode.schedule.attachedToDay') }}
                    </span>
                </div>

                <template v-if="d.on">
                    <div v-for="(slot, si) in d.slots" :key="'slot-' + si">
                        <!-- Pause entre deux créneaux -->
                        <div v-if="si > 0" class="ppv2-flex ppv2-items-center" style="gap: 10px; padding: 6px 0 6px 4px">
                            <span style="width: 2px; height: 20px; border-left: 2px dashed #cbd2dc"></span>
                            <span class="sde-pause-chip poppinsMedium">{{ pauseLabel(d, si) }}</span>
                        </div>

                        <div class="ppv2-flex ppv2-items-center" style="gap: 14px">
                            <TimeSelect
                                :value="getSlotValue(d, si, 0)"
                                :min="fieldBounds(di, si, false).min"
                                :max="fieldBounds(di, si, false).max"
                                :step="gridStep"
                                :max-options="maxStartMinutes"
                                :width-px="186"
                                :show-next-day-badge="false"
                                @change="(val) => setTime(di, si, 0, val)"
                            />
                            <TimeSelect
                                :value="getSlotValue(d, si, 1)"
                                :min="fieldBounds(di, si, true).min"
                                :max="fieldBounds(di, si, true).max"
                                :step="gridStep"
                                :max-options="si === d.slots.length - 1 ? maxEndMinutes : maxStartMinutes"
                                :promote-below="getSlotValue(d, si, 0)"
                                :width-px="186"
                                :show-next-day-badge="false"
                                @change="(val) => setTime(di, si, 1, val)"
                            />
                            <span class="sde-duration ppv2-tabular-nums">{{ durationLabel(d, si) }}</span>

                            <button
                                v-if="d.slots.length > 1"
                                type="button"
                                class="sde-icon-btn"
                                :title="$t('experience.step4.multiMode.schedule.removeSlot')"
                                :aria-label="$t('experience.step4.multiMode.schedule.removeSlot')"
                                @click="removeSecondSlot(di)"
                            >
                                <X :size="14" />
                            </button>

                            <!-- Actions (premier créneau uniquement) -->
                            <div v-if="si === 0" class="ppv2-flex ppv2-items-center" style="gap: 8px; margin-left: auto">
                                <!-- Le jour qui reçoit la nuit de la veille n'a plus de pause disponible
                                     (une seule pause par jour côté backend, déjà prise par la nuit). -->
                                <span
                                    v-if="pauseBlockedByNight(di)"
                                    class="sde-pause-unavailable poppinsMedium"
                                    :title="$t('experience.step4.multiMode.schedule.pauseUnavailableHint')"
                                >
                                    <Info :size="13" />
                                    {{ $t('experience.step4.multiMode.schedule.pauseUnavailable') }}
                                </span>
                                <!-- Second créneau proposable si l'amplitude le permet (≥ 30 + 30 + 30 + 30). -->
                                <button
                                    v-else-if="canSplit(di)"
                                    type="button"
                                    class="sde-icon-btn"
                                    :title="$t('experience.step4.multiMode.schedule.addSlot')"
                                    :aria-label="$t('experience.step4.multiMode.schedule.addSlot')"
                                    @click="splitIntoTwo(di)"
                                >
                                    <Plus :size="15" />
                                </button>

                                <!-- Appliquer à tous les jours : vert « Appliqué » pendant ~2 s -->
                                <button
                                    type="button"
                                    class="sde-icon-btn"
                                    :class="{ 'sde-icon-btn--applied poppinsSemiBold': isAppliedToAll(d) }"
                                    :title="$t('experience.step4.multiMode.schedule.applyToAll')"
                                    :aria-label="$t('experience.step4.multiMode.schedule.applyToAll')"
                                    @click="applyToAll(d)"
                                >
                                    <template v-if="isAppliedToAll(d)">
                                        <Check :size="15" />
                                        {{ $t('experience.step4.multiMode.schedule.applied') }}
                                    </template>
                                    <Copy v-else :size="15" />
                                </button>

                                <!-- Copier vers certains jours : page établissement seulement (absent de l'écran 5a) -->
                                <button
                                    v-if="allowCopyToDays"
                                    type="button"
                                    class="sde-icon-btn ppv2-gap-1"
                                    :class="{ 'sde-icon-btn--active': copyingDayName === d.name }"
                                    :title="$t('experience.step4.multiMode.schedule.copyToSelected')"
                                    :aria-label="$t('experience.step4.multiMode.schedule.copyToSelected')"
                                    @click="toggleCopyPanel(d)"
                                >
                                    <ListCheck :size="16" />
                                    <span
                                        class="ppv2-text-sm"
                                        v-if="copyingDayName === d.name && selectedTargetDays.length > 0"
                                        >{{ selectedTargetDays.length }}</span
                                    >
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Expandable panel for copying to selected days -->
                    <div
                        v-if="allowCopyToDays && copyingDayName === d.name"
                        class="copy-target-panel ppv2-mt-3 ppv2-p-3 ppv2-bg-white ppv2-rounded-[12px] ppv2-border ppv2-border-[#CBD2DC] ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-3"
                    >
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-flex-wrap">
                            <span class="ppv2-text-[13px] ppv2-text-[#64748B] poppinsMedium ppv2-mr-1">
                                {{ $t('experience.step4.multiMode.schedule.copyTo') }}
                            </span>
                            <button
                                v-for="targetDay in otherDays(d)"
                                :key="'target-' + targetDay.name"
                                type="button"
                                class="target-day-pill ppv2-transition-all"
                                :class="
                                    isTargetSelected(targetDay.name)
                                        ? 'selected ppv2-bg-[#7D2DBD] ppv2-border-[#7D2DBD] !ppv2-text-[#fff] poppinsSemiBold'
                                        : 'ppv2-bg-white dark:ppv2-bg-dark ppv2-border-[#CBD2DC] ppv2-text-black hover:ppv2-bg-opacity-80 poppinsMedium'
                                "
                                @click="toggleTargetDay(targetDay.name)"
                            >
                                {{ shortDayLabel(targetDay) }}
                            </button>
                        </div>

                        <button
                            type="button"
                            class="ppv2-px-5 ppv2-h-[34px] ppv2-bg-[#7D2DBD] hover:ppv2-bg-[#6c24a6] ppv2-rounded-full !ppv2-text-white poppinsMedium ppv2-text-[13px] ppv2-flex-shrink-0 ppv2-transition-colors"
                            :disabled="selectedTargetDays.length === 0"
                            :class="selectedTargetDays.length === 0 ? 'ppv2-opacity-50 ppv2-cursor-not-allowed' : ''"
                            @click="applyToSelected(d)"
                        >
                            {{ $t('experience.step4.multiMode.schedule.apply') }}
                        </button>
                    </div>
                </template>

                <!-- Jour fermé -->
                <div v-else class="ppv2-flex ppv2-items-center" style="height: 38px">
                    <span class="sde-closed poppinsRegular">{{ $t('common.scheduleTime.closedAllDay') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as hours from '../../../../utils/scheduleHours'
import TimeSelect from '../../../Shared/TimeSelect.vue'
import { Check, Copy, Info, ListCheck, Moon, Plus, X } from 'lucide-vue'

/**
 * Éditeur hebdomadaire d'horaires (modale Étape 4 expérience + horaires établissement).
 * Modèle de travail : `draftDays[] = { name, label, day, on, slots: [[start, end]], nightFromPrev }`
 * en minutes, une fin > 1440 = « le lendemain » (dernier créneau, ≤ 05:00). Toute la logique
 * (bornes croisées entre jours, « Pause indisponible » sur le jour qui reçoit une nuit, encodage
 * wire D 23:59 / D+1 00:00 + pause) vit dans utils/scheduleHours.js ; ce composant ne fait
 * qu'afficher et renormaliser le brouillon après chaque mutation.
 */
export default {
    name: 'ScheduleDaysEditor',
    components: { TimeSelect, Check, Copy, Info, ListCheck, Moon, Plus, X },
    props: {
        days: { type: Array, required: true },
        // « Copier vers certains jours » : horaires établissement (écran 2a) ; absent de la modale expérience (5a).
        allowCopyToDays: { type: Boolean, default: false },
    },
    data() {
        return {
            gridStep: hours.GRID_STEP,
            maxStartMinutes: hours.MAX_DAY_MINUTES,
            maxEndMinutes: hours.OVERNIGHT_MAX_MINUTES,
            draftDays: [],
            appliedDayId: null,
            appliedTimer: null,
            copyingDayName: null,
            selectedTargetDays: [],
        }
    },
    watch: {
        days: {
            handler() {
                this.initDraft()
            },
            deep: true,
        },
    },
    mounted() {
        this.initDraft()
    },
    beforeDestroy() {
        clearTimeout(this.appliedTimer)
    },
    methods: {
        initDraft() {
            this.draftDays = hours.normalizeWeek(hours.parseWeek(this.days || []))
            this.appliedDayId = null
            this.copyingDayName = null
            this.selectedTargetDays = []
            this.emitChange()
        },
        emitChange() {
            this.$emit('change', this.draftDays)
        },
        /** Renormalise le brouillon (bornes, nuit précédente tronquée) puis notifie le parent. */
        commitDraft() {
            this.draftDays = hours.normalizeWeek(this.draftDays)
            this.emitChange()
        },
        toggleDay(d) {
            d.on = !d.on
            this.onToggleDay(d)
        },
        onToggleDay(d) {
            if (d.on && !(d.slots || []).some((s) => hours.slotDuration(s) >= hours.MIN_SLOT_MINUTES)) {
                d.slots = [[hours.DEFAULT_SLOT[0], hours.DEFAULT_SLOT[1]]]
            }
            this.commitDraft()
        },
        dayRowStyle() {
            return {
                gridTemplateColumns: '150px 1fr',
                alignItems: 'start',
                gap: '18px',
                padding: '14px 0',
                borderColor: '#EEF1F5',
            }
        },
        prevDay(di) {
            if (!this.draftDays || this.draftDays.length !== 7) return null
            return this.draftDays[(di + 6) % 7]
        },
        getPreviousDayNight(di) {
            const prev = this.prevDay(di)
            const nightEnd = hours.nightEndOf(prev)
            if (nightEnd === null) return null
            return {
                dayLabel: prev.label || prev.name,
                endTime: hours.minutesToHHMM(nightEnd),
            }
        },
        getSlotValue(d, si, field) {
            const slot = d.slots[si]
            return slot ? slot[field] : 0
        },
        setTime(di, si, field, minutes) {
            const d = this.draftDays[di]
            if (d && d.slots && d.slots[si]) {
                this.$set(d.slots[si], field, minutes)
                this.commitDraft()
            }
        },
        fieldBounds(di, si, isEnd) {
            return hours.weekSlotBounds(this.draftDays, di, si, isEnd)
        },
        canSplit(di) {
            return hours.canSplitDay(this.draftDays, di)
        },
        pauseBlockedByNight(di) {
            return hours.pauseBlockedByNight(this.draftDays, di)
        },
        splitIntoTwo(di) {
            if (!this.canSplit(di)) return
            const d = this.draftDays[di]
            d.slots = hours.splitSlot(d.slots)
            this.commitDraft()
        },
        removeSecondSlot(di) {
            const d = this.draftDays[di]
            if (!d || !d.slots || d.slots.length <= 1) return
            d.slots = hours.mergeSlots(d.slots)
            this.commitDraft()
        },
        durationLabel(d, si) {
            const slot = d.slots[si]
            if (!slot) return ''
            return hours.minutesReadable(hours.slotDuration(slot))
        },
        pauseLabel(d, si) {
            const prev = d.slots[si - 1]
            const current = d.slots[si]
            if (!prev || !current) return ''
            const pause = current[0] - prev[1]
            if (pause <= 0) return ''
            return this.$t('experience.step4.multiMode.schedule.pause') + ' ' + hours.minutesReadable(pause)
        },
        isAppliedToAll(d) {
            return this.appliedDayId === d.name
        },
        applyToAll(d) {
            clearTimeout(this.appliedTimer)
            const copied = d.slots.map((s) => [s[0], s[1]])
            this.draftDays.forEach((day) => {
                if (day.name === d.name) return
                day.on = true
                day.slots = copied.map((s) => [s[0], s[1]])
            })
            this.appliedDayId = d.name
            this.appliedTimer = setTimeout(() => {
                if (this.appliedDayId === d.name) this.appliedDayId = null
            }, 2000)
            this.commitDraft()
        },
        toggleCopyPanel(d) {
            if (this.copyingDayName === d.name) {
                this.copyingDayName = null
                this.selectedTargetDays = []
            } else {
                this.copyingDayName = d.name
                this.selectedTargetDays = []
            }
        },
        otherDays(d) {
            return this.draftDays.filter((day) => day.name !== d.name)
        },
        shortDayLabel(day) {
            const map = {
                monday: 'Lun',
                tuesday: 'Mar',
                wednesday: 'Mer',
                thursday: 'Jeu',
                friday: 'Ven',
                saturday: 'Sam',
                sunday: 'Dim',
            }
            return map[day.name] || (day.label ? day.label.substring(0, 3) : '')
        },
        isTargetSelected(name) {
            return this.selectedTargetDays.includes(name)
        },
        toggleTargetDay(name) {
            const idx = this.selectedTargetDays.indexOf(name)
            if (idx >= 0) {
                this.selectedTargetDays.splice(idx, 1)
            } else {
                this.selectedTargetDays.push(name)
            }
        },
        applyToSelected(d) {
            if (this.selectedTargetDays.length === 0) return
            const copied = d.slots.map((s) => [s[0], s[1]])
            this.draftDays.forEach((day) => {
                if (this.selectedTargetDays.includes(day.name)) {
                    day.on = true
                    day.slots = copied.map((s) => [s[0], s[1]])
                }
            })
            this.copyingDayName = null
            this.selectedTargetDays = []
            this.commitDraft()
        },
        save() {
            this.$emit('save', hours.serializeWeek(hours.normalizeWeek(this.draftDays)))
        },
    },
}
</script>

<style scoped>
/* Cotes et couleurs : maquette 5a (artifact MSnHmGcueYzToM4iDsYCTN). */
.sde-switch {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    flex: none;
    width: 33px;
    height: 19px;
    padding: 2px;
    border: none;
    border-radius: 999px;
    background: #cbd2dc;
    cursor: pointer;
    transition: background 0.15s ease;
}
.sde-switch--on {
    justify-content: flex-end;
    background: #7d2dbd;
}
.sde-switch__knob {
    display: block;
    flex: none;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(2, 24, 50, 0.25);
}

.sde-day-name {
    font-size: 14.5px;
    color: #021832;
    white-space: nowrap;
}
.sde-day-name--off,
.sde-closed,
.sde-duration {
    color: #9aa4b2;
}
.sde-closed {
    font-size: 13.5px;
    white-space: nowrap;
}
.sde-duration {
    font-size: 12.5px;
    white-space: nowrap;
}

.sde-night-banner {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 8px 12px;
    margin-bottom: 6px;
    background: #f8f9fb;
    border: 1px dashed #cbd2dc;
    border-radius: 10px;
    font-size: 11.5px;
    color: #6b7480;
}

.sde-pause-chip {
    font-size: 11.5px;
    white-space: nowrap;
    color: #6b7480;
    background: #eef1f5;
    padding: 3px 10px;
    border-radius: 999px;
}

.sde-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    min-width: 32px;
    height: 32px;
    padding: 0;
    color: #6b7480;
    background: #fff;
    border: 1px solid #e2e6ec;
    border-radius: 9px;
    cursor: pointer;
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease;
}
.sde-icon-btn:hover {
    color: #021832;
    background: #f8f9fb;
    border-color: #cbd2dc;
}
.sde-icon-btn--applied,
.sde-icon-btn--applied:hover {
    gap: 6px;
    padding: 0 11px;
    font-size: 12px;
    white-space: nowrap;
    color: #1c7a4a;
    background: #e8f6ee;
    border-color: #9ed9ba;
}
.sde-icon-btn--active,
.sde-icon-btn--active:hover {
    padding: 0 8px;
    color: #fff;
    background: #7d2dbd;
    border-color: #7d2dbd;
}

.sde-pause-unavailable {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    white-space: nowrap;
    color: #571f84;
    background: #f3eafa;
    border: 1px solid #dbc1f0;
    border-radius: 9px;
    padding: 6px 10px;
}

.target-day-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 48px;
    padding: 0 12px;
    border-radius: 9999px;
    border-width: 1px;
    border-style: solid;
    font-size: 13px;
    cursor: pointer;
}

.target-day-pill.selected {
    border-width: 1.5px;
}

.dark .sde-day-name {
    color: #fff;
}
.dark .sde-day-name--off {
    color: #9aa4b2;
}
.dark .sde-night-banner {
    background-color: #1a1f3d;
    border-color: #2b3362;
    color: #a8b0cc;
}
.dark .sde-pause-chip {
    background: #2b3362;
    color: #a8b0cc;
}
.dark .sde-icon-btn {
    background: transparent;
    border-color: #2b3362;
    color: #a8b0cc;
}
</style>
